/**
 * industryPages.mjs
 *
 * Verified-content-only industry-page data.
 *
 * Every capability referenced here is grounded in what the live Swipetronics
 * site already claims:
 *   - src/lib/terminals.js (Valor, Dejavoo, PAX hardware catalog + specs)
 *   - src/pages/Industries.jsx (short industry positioning already on prod)
 *   - src/pages/Services.jsx / PosSystems.jsx / Products.jsx / CashDiscount.jsx
 *     / FreePos.jsx / MobileDevices.jsx / Ecommerce.jsx
 *
 * Wording rules applied:
 *   - "card processing" instead of "credit card processing"
 *   - "eligible businesses may qualify" / "available options" / "depending on
 *     the setup" for Cash Discount and Free POS
 *   - No guarantees of compliance, approval, savings percentages, or
 *     invented POS features / integrations / hardware / pricing.
 *   - Smoke Shops: legitimate POS + payment scope only, no regulatory
 *     compliance guarantees.
 */

export const INDUSTRY_ORDER = [
  "restaurants",
  "convenience-stores-delis",
  "grocery-stores",
  "smoke-shops",
  "retail-stores",
  "salons-spas",
  "service-businesses",
];

export const INDUSTRY_PAGES = {
  /* ══════════════════════════════════════════════════════════════════ */
  "restaurants": {
    slug: "restaurants",
    illustrationKey: "restaurants",
    seo: {
      title: "Restaurant POS & Card Processing | Swipetronics",
      description:
        "Card processing, tipping, and POS setups for restaurants — fast checkout, split checks, and tip handling with terminals built for the rush.",
    },
    eyebrow: "For Restaurants",
    h1: "Payment solutions for restaurants",
    heroBody:
      "Restaurants need payment technology that keeps up with a busy dining room — fast card processing, clean tip handling, and equipment that doesn't slow the line at the register.",
    challengesHeading: "What makes payments harder in a restaurant",
    challenges: [
      "Turning tables quickly without long checkout waits or clunky tip flows.",
      "Handling split checks, tips added at the terminal, and cash and card in the same transaction.",
      "Choosing hardware that works for both counter service and tableside handoffs.",
      "Keeping card processing costs predictable so margins aren't eaten by fees.",
    ],
    howHeading: "How restaurants commonly accept payments",
    howBody:
      "Most restaurants take a mix of countertop checkout, portable/handheld payments for tableside handoffs, and — for many concepts — online orders or delivery. Swipetronics can help pick a setup that fits the venue.",
    howPoints: [
      { t: "At the counter", d: "Countertop terminals for quick-service and fast-casual concepts." },
      { t: "At the table",   d: "Handheld/wireless devices so guests can pay without waiting for a check-back." },
      { t: "Off-premise",     d: "Card processing that pairs with online ordering, delivery apps, or a hosted checkout." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing built for restaurants",
        body:  "Card processing configured for tip prompts, contactless payments, and predictable pricing.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Modern point-of-sale systems suited to the mix of counter, tableside, and takeout.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop and handheld terminals from brands like Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "For eligible restaurants, a Cash Discount program can help reduce the cost of card processing.",
        to:    "/cash-discount" },
      { title: "Free POS Program",
        body:  "Eligible restaurants may qualify for a modern POS without a large upfront equipment cost.",
        to:    "/free-pos-program" },
    ],
    hardwareHeading: "Terminals commonly used in restaurants",
    hardwareBody:
      "The right terminal depends on how each restaurant operates. A quick-service counter has different needs than a full-service dining room. During your setup, we'll match your business to a specific device.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "Fast at the register with tip prompts, EMV chip, contactless tap, and swipe (e.g., Valor VL100)." },
      { title: "Handheld / wireless",    body: "For tableside checkout and mobility around the dining room (e.g., Valor VL110)." },
      { title: "Smart terminals",         body: "Touchscreen devices that support tipping and additional features (e.g., Valor VP550)." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "Faster table turns when tip and split-check flows are set up correctly at the terminal.",
      "Fewer checkout stalls when the equipment fits the way the restaurant actually operates.",
      "Predictable processing costs so pricing math on the menu still holds up at month-end.",
    ],
    faqs: [
      { q: "Do the terminals support tips at the point of sale?",
        a: "Yes — tip prompts are a standard feature on the smart terminals we work with (for example, Valor VP550). Tip handling is set up during onboarding." },
      { q: "Can we accept contactless / tap-to-pay?",
        a: "Yes. The terminals we offer support EMV chip, contactless (NFC), and swipe. Specific acceptance depends on the model." },
      { q: "Can a restaurant use the Cash Discount program?",
        a: "Cash Discount / Dual Pricing may be a fit for eligible restaurants. Whether it's the right choice depends on how the restaurant prices its menu and communicates with guests — we can walk you through the tradeoffs before deciding." },
      { q: "Does Swipetronics work with online ordering?",
        a: "Card processing can be paired with a hosted checkout, cart plugins, or virtual terminal for orders taken online or over the phone. The specific integration depends on the platform your restaurant uses." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "convenience-stores-delis": {
    slug: "convenience-stores-delis",
    illustrationKey: "delis",
    seo: {
      title: "Convenience Store & Deli POS + Card Processing | Swipetronics",
      description:
        "Card processing and POS setups for convenience stores and delis — fast scanning, tap-to-pay, and terminal options built for high transaction volume.",
    },
    eyebrow: "For Convenience Stores & Delis",
    h1: "Payment solutions for convenience stores and delis",
    heroBody:
      "Convenience stores and delis run on speed. Every second at the register matters when there are five customers in line — so the payment setup has to be quick, dependable, and easy to train new staff on.",
    challengesHeading: "What makes payments harder in a c-store or deli",
    challenges: [
      "High transaction volume with small ticket sizes — processing fees add up fast.",
      "A mix of quick items (drink, snack, lottery) and slower items (deli order, sandwich).",
      "Age-restricted product sales that need a clean prompt at checkout to help staff verify age.",
      "Cash-heavy environments where a Cash Discount program can meaningfully reduce card-processing costs for eligible businesses.",
    ],
    howHeading: "How c-stores and delis commonly accept payments",
    howBody:
      "Most c-stores rely on a countertop or smart terminal at the register that supports fast tap-to-pay along with chip and swipe. Delis often add a printer for kitchen/order tickets and may benefit from a POS with quick-select buttons for common items.",
    howPoints: [
      { t: "At the register", d: "Countertop / smart terminals with tap, chip, and swipe acceptance." },
      { t: "For deli orders",   d: "POS systems that support quick-item entry and receipt printing." },
      { t: "For age-restricted items", d: "Setups that can prompt staff to verify age at checkout." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing built for high transaction volume with tap, chip, and swipe support.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Modern point-of-sale systems for c-stores and delis with quick-item entry.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop terminals with fast checkout — brands include Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "Reduce the cost of card processing on eligible businesses. POS prompts, signage, and receipts are configured to program rules.",
        to:    "/cash-discount" },
      { title: "Free POS Program",
        body:  "Eligible c-stores and delis may qualify for a modern POS without a large upfront equipment cost.",
        to:    "/free-pos-program" },
    ],
    hardwareHeading: "Terminals commonly used in c-stores and delis",
    hardwareBody:
      "We'll match the equipment to the store's actual needs during setup. Some locations prefer a full POS; others just need a dependable countertop terminal.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "Fast at the register with tip prompts, EMV chip, contactless, and swipe (e.g., Valor VL100, Dejavoo Z-series)." },
      { title: "Smart terminals",         body: "Touchscreen models with additional configurable features (e.g., Valor VP550)." },
      { title: "Full POS setups",         body: "Where the store benefits from quick-item entry, receipt printing, and category buttons." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "Every second saved at checkout means shorter lines and fewer walk-outs during busy periods.",
      "Card-processing costs stack up on small-ticket sales — the right pricing model can meaningfully change monthly totals.",
      "Well-configured age-restricted-item prompts help staff stay consistent at checkout.",
    ],
    faqs: [
      { q: "Can the terminal prompt for age verification on restricted items?",
        a: "POS and terminal setups can include prompts at checkout to help staff verify age on restricted items. The specifics depend on the equipment configuration — we'll walk through what your store needs during setup." },
      { q: "Is Cash Discount a fit for c-stores and delis?",
        a: "For eligible businesses, Cash Discount / Dual Pricing can reduce card-processing costs. It works best when the store is comfortable posting both prices clearly and communicating the program to customers." },
      { q: "Do the terminals support tap-to-pay?",
        a: "Yes. The terminals we offer support EMV chip, contactless (NFC / tap), and swipe. Specific acceptance depends on the model." },
      { q: "Can we accept EBT?",
        a: "EBT acceptance is available on select terminals (for example, Dejavoo models with EBT support). Whether it makes sense for your store depends on your product mix and eligibility." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "grocery-stores": {
    slug: "grocery-stores",
    illustrationKey: "grocery",
    seo: {
      title: "Grocery Store Card Processing & POS | Swipetronics",
      description:
        "Card processing and POS setups for grocery stores — EBT acceptance on select terminals, tap-to-pay, and equipment options for single- or multi-lane checkout.",
    },
    eyebrow: "For Grocery Stores",
    h1: "Payment solutions for grocery stores",
    heroBody:
      "Grocery stores need payment technology that keeps every lane moving — fast card processing, EBT acceptance where relevant, and dependable equipment that can handle the daily transaction volume.",
    challengesHeading: "What makes payments harder in a grocery store",
    challenges: [
      "Keeping every checkout lane moving during rush hours without dropped transactions.",
      "Accepting a mix of tender types — card, contactless, and EBT where applicable.",
      "Managing card-processing costs across a high volume of small- to mid-ticket sales.",
      "Choosing equipment that scales from a single lane to a multi-lane setup.",
    ],
    howHeading: "How grocery stores commonly accept payments",
    howBody:
      "Most grocery stores use countertop or smart terminals paired with a POS at each lane, supporting tap, chip, swipe, and — where applicable — EBT. The right setup depends on whether the store runs a single lane or multiple.",
    howPoints: [
      { t: "Single-lane", d: "Countertop terminal at the register with fast tap / chip / swipe." },
      { t: "Multi-lane",   d: "POS-driven checkout at each lane with card processing at every station." },
      { t: "EBT acceptance", d: "Available on select terminals — configured for eligible stores." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing configured for grocery — tap, chip, swipe, and EBT on select models.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Point-of-sale systems that can be deployed on one lane or multiple lanes.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop and smart terminals — brands include Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "Reduce the cost of card processing on eligible grocery businesses.",
        to:    "/cash-discount" },
      { title: "Free POS Program",
        body:  "Eligible grocery stores may qualify for a modern POS without a large upfront equipment cost.",
        to:    "/free-pos-program" },
    ],
    hardwareHeading: "Terminals commonly used in grocery stores",
    hardwareBody:
      "The right hardware depends on store size, number of lanes, and whether EBT is a required tender. We'll match a specific device during setup.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "For single- or dual-lane stores that need dependable checkout." },
      { title: "Smart terminals",         body: "Touchscreen models with additional features and cleaner interfaces." },
      { title: "EBT-capable terminals",   body: "EBT acceptance is available on select terminals (for example, certain Dejavoo models)." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "Faster lanes during peak hours reduce cart abandonment and keep customers coming back.",
      "The right pricing model can meaningfully change what a grocery store pays in card-processing costs.",
      "EBT-capable equipment lets eligible stores serve more customers cleanly at checkout.",
    ],
    faqs: [
      { q: "Can grocery stores accept EBT with Swipetronics?",
        a: "EBT acceptance is available on select terminals — Dejavoo models, for example, support EBT on select devices. Whether it makes sense for your store depends on your product mix and store eligibility." },
      { q: "Do the terminals support tap-to-pay?",
        a: "Yes. The terminals we offer support EMV chip, contactless (NFC / tap), and swipe. Specific acceptance depends on the model." },
      { q: "Is Cash Discount a fit for a grocery store?",
        a: "Cash Discount / Dual Pricing may be a fit for eligible grocery businesses. Whether it's the right choice depends on the store's shelf-price strategy and how it communicates the program to customers." },
      { q: "Can we run more than one lane on the same account?",
        a: "Yes. Multi-lane setups are common in grocery, and we'll match the right combination of terminal and POS during onboarding." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "smoke-shops": {
    slug: "smoke-shops",
    illustrationKey: "smoke-shops",
    seo: {
      title: "Smoke Shop POS & Card Processing | Swipetronics",
      description:
        "Card processing, POS options, and Cash Discount setups for eligible smoke shops. Payment terminals with tap, chip, and swipe on select models.",
    },
    eyebrow: "For Smoke Shops",
    h1: "Payment solutions for smoke shops",
    heroBody:
      "Smoke shops have specific card-processing and POS needs — from setting up a Cash Discount program correctly, to configuring the register for age-restricted items, to keeping the counter moving during peak hours.",
    challengesHeading: "What makes payments harder in a smoke shop",
    challenges: [
      "Card-processing fees stacking up quickly on a high transaction volume with typically smaller-ticket sales.",
      "Age-restricted product sales that need a clean prompt at checkout so staff can consistently verify age.",
      "Choosing terminal hardware that pairs well with a Cash Discount / Dual Pricing program if the shop is eligible.",
      "Balancing speed at checkout with clear price display and receipt language required by the program in use.",
    ],
    howHeading: "How smoke shops commonly accept payments",
    howBody:
      "Most smoke shops run a countertop or smart terminal at the register, configured for tap, chip, and swipe. Many use a Cash Discount / Dual Pricing setup to help offset card-processing costs — but eligibility, signage, and receipt language all matter.",
    howPoints: [
      { t: "At the counter", d: "Countertop and smart terminals with fast tap / chip / swipe acceptance." },
      { t: "With Cash Discount", d: "POS prompts, signage, and receipts configured to program rules for eligible shops." },
      { t: "For age-restricted items", d: "Setups that can prompt staff to verify age at checkout." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing with tap, chip, and swipe support — configured for the shop's actual workflow.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Point-of-sale systems for the counter with quick-item entry and receipt printing.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop and smart terminals — Valor, Dejavoo, and PAX options depending on the setup.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "For eligible shops, we can configure a compliant Cash Discount program with matching POS prompts and receipts.",
        to:    "/cash-discount" },
    ],
    hardwareHeading: "Terminals commonly used in smoke shops",
    hardwareBody:
      "We'll match the equipment to what the shop actually needs during setup. Cash-Discount-friendly countertop terminals are a common starting point.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "Cash-Discount-friendly countertop options (e.g., Valor VL100) for fast checkout." },
      { title: "Smart terminals",         body: "Touchscreen models with additional configurable features (e.g., Valor VP550)." },
      { title: "Full POS setups",         body: "Where the shop benefits from category buttons, receipt printing, and staff prompts." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "A properly configured Cash Discount program can meaningfully change monthly card-processing costs for eligible shops.",
      "Consistent staff prompts at checkout help the shop stay disciplined on age-restricted-item sales.",
      "Fast, dependable checkout keeps the counter moving during peak hours.",
    ],
    // Extra compliance disclaimer for this vertical
    disclaimer:
      "Swipetronics does not guarantee approval for every smoke shop or every product type, and does not act as a legal or regulatory advisor. Age-verification prompts, program signage, and receipt language are payment-setup features — the shop is responsible for complying with all applicable laws in its jurisdiction.",
    faqs: [
      { q: "Can smoke shops use Cash Discount / Dual Pricing?",
        a: "For eligible smoke shops, a Cash Discount / Dual Pricing setup is a common way to help reduce card-processing costs. Eligibility, signage, and receipt configuration all matter — we can walk you through what's involved before setup." },
      { q: "Does the POS help with age verification at checkout?",
        a: "POS and terminal configurations can include prompts at checkout to help staff verify age on restricted items. Prompts help staff stay consistent — the shop remains responsible for compliance in its jurisdiction." },
      { q: "Do all smoke shops qualify for payment processing?",
        a: "Not every business qualifies for every processing setup. Approval depends on the shop, product mix, and other factors — we can help determine what's realistic before moving forward." },
      { q: "Which terminals support Cash Discount programs?",
        a: "Several of the terminals we work with are Cash-Discount-friendly (for example, Valor VL100 and Valor VP550). We'll match a specific device during setup." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "retail-stores": {
    slug: "retail-stores",
    illustrationKey: "retail",
    seo: {
      title: "Retail Store POS & Card Processing | Swipetronics",
      description:
        "Card processing and POS setups for retail stores — tap-to-pay, receipt options, and terminal choices for boutique, apparel, and specialty retail.",
    },
    eyebrow: "For Retail Stores",
    h1: "Payment solutions for retail stores",
    heroBody:
      "Retail stores need payment technology that looks and feels as polished as the storefront — fast card processing, clean receipts, and terminal hardware that fits the counter design.",
    challengesHeading: "What makes payments harder in a retail store",
    challenges: [
      "Balancing checkout speed with a customer experience that fits the brand of the store.",
      "Managing a wide range of ticket sizes — from a single item to a large multi-item purchase.",
      "Choosing equipment that fits the counter aesthetic without sacrificing acceptance or reliability.",
      "Keeping processing costs predictable across seasons of higher and lower volume.",
    ],
    howHeading: "How retail stores commonly accept payments",
    howBody:
      "Most retail stores use a countertop or smart terminal at the register, paired with card acceptance for tap, chip, and swipe. Larger stores may add a POS for receipts and printed customer copies.",
    howPoints: [
      { t: "At the counter", d: "Countertop or smart terminals with fast tap / chip / swipe acceptance." },
      { t: "For online orders", d: "Card processing that pairs with a hosted checkout or cart plugin for the store's website." },
      { t: "For gift-card acceptance", d: "Gift card acceptance is available depending on the setup." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing configured for retail — tap, chip, swipe, and digital wallet acceptance.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Point-of-sale systems for the counter with clean receipt handling.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop and smart terminals from Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "For eligible retail businesses, Cash Discount can help reduce card-processing costs.",
        to:    "/cash-discount" },
      { title: "Free POS Program",
        body:  "Eligible retail stores may qualify for a modern POS without a large upfront equipment cost.",
        to:    "/free-pos-program" },
    ],
    hardwareHeading: "Terminals commonly used in retail",
    hardwareBody:
      "The right terminal depends on how the counter is set up and how the store handles receipts and returns. We'll match a specific device during onboarding.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "Fast at the register with EMV chip, contactless, and swipe (e.g., Valor VL100)." },
      { title: "Smart terminals",         body: "Touchscreen models with clean interfaces and additional configurable features (e.g., Valor VP550)." },
      { title: "Pin pad devices",         body: "Where the counter benefits from a separate customer-facing pin pad (e.g., Valor VL300)." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "A polished checkout experience matters — the terminal is often the last thing a customer touches before leaving the store.",
      "Reliable tap and chip acceptance reduces friction, especially during holiday and event traffic.",
      "The right pricing model helps keep processing costs predictable across busy and slow seasons.",
    ],
    faqs: [
      { q: "Do the terminals support digital wallets like Apple Pay / Google Pay?",
        a: "Yes. Contactless (NFC) acceptance on the terminals we offer supports digital wallets. Specific acceptance depends on the model." },
      { q: "Can we accept online orders on the same account?",
        a: "Yes. Card processing can be paired with a hosted checkout or cart plugin for orders taken online — the specific integration depends on the platform your store uses." },
      { q: "Is Cash Discount a fit for a retail store?",
        a: "Cash Discount / Dual Pricing may be a fit for eligible retail businesses. Whether it's the right choice depends on the store's brand and how it communicates pricing to customers." },
      { q: "Which terminal is right for a small boutique?",
        a: "That depends on the counter layout, receipt needs, and whether the boutique wants a separate customer-facing pin pad. We'll match a specific device during setup." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "salons-spas": {
    slug: "salons-spas",
    illustrationKey: "salons",
    seo: {
      title: "Salon & Spa POS + Card Processing | Swipetronics",
      description:
        "Card processing and POS setups for salons and spas — tip handling, clean receipts, and terminal options built for service-based businesses.",
    },
    eyebrow: "For Salons & Spas",
    h1: "Payment solutions for salons and spas",
    heroBody:
      "Salons and spas need payment technology that handles service pricing and tips cleanly — from the front desk to the individual stylist, technician, or therapist.",
    challengesHeading: "What makes payments harder in a salon or spa",
    challenges: [
      "Handling tips at the terminal cleanly so each service provider is credited correctly.",
      "Supporting different service prices, add-ons, and packages without slowing down the front desk.",
      "Choosing a terminal that fits the aesthetic of the front counter.",
      "Keeping card-processing costs predictable across a mix of card and cash tenders.",
    ],
    howHeading: "How salons and spas commonly accept payments",
    howBody:
      "Most salons and spas run a countertop or smart terminal at the front desk configured for tip prompts, and pair it with card acceptance for tap, chip, and swipe.",
    howPoints: [
      { t: "At the front desk", d: "Countertop or smart terminals with tip prompts and fast checkout." },
      { t: "For gift cards",       d: "Gift card acceptance is available depending on the setup." },
      { t: "For online booking",   d: "Card processing can pair with online booking platforms when the platform supports it." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing with tip prompts and tap / chip / swipe acceptance configured at setup.",
        to:    "/services" },
      { title: "POS systems",
        body:  "Point-of-sale systems for the front desk that handle service selection and receipts.",
        to:    "/pos-systems" },
      { title: "Payment terminals",
        body:  "Countertop and smart terminals from Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Cash Discount / Dual Pricing",
        body:  "For eligible salons and spas, Cash Discount can help reduce card-processing costs.",
        to:    "/cash-discount" },
      { title: "Free POS Program",
        body:  "Eligible salons and spas may qualify for a modern POS without a large upfront equipment cost.",
        to:    "/free-pos-program" },
    ],
    hardwareHeading: "Terminals commonly used in salons and spas",
    hardwareBody:
      "The right terminal depends on the size of the front desk, whether the business wants a customer-facing pin pad, and how it handles tips.",
    hardwareItems: [
      { title: "Countertop terminals",  body: "Fast at the front desk with tip prompts, EMV chip, contactless, and swipe (e.g., Valor VL100)." },
      { title: "Smart terminals",         body: "Touchscreen models with clean interfaces and additional features (e.g., Valor VP550)." },
      { title: "Pin pad devices",         body: "For salons that want a customer-facing pin pad separate from the operator terminal (e.g., Valor VL300)." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "Clean tip prompts at the terminal keep tips flowing correctly to each service provider.",
      "A polished checkout experience adds to the guest-service experience you've already built.",
      "The right pricing model helps keep card-processing costs predictable.",
    ],
    faqs: [
      { q: "Do the terminals handle tips at checkout?",
        a: "Yes — tip prompts are a standard feature on the smart terminals we work with (for example, Valor VP550). Tip flow is set up during onboarding." },
      { q: "Can the terminal integrate with online booking?",
        a: "Card processing can pair with online booking platforms when the platform supports it. The specifics depend on the platform your salon or spa is on today." },
      { q: "Is Cash Discount a fit for a salon or spa?",
        a: "Cash Discount / Dual Pricing may be a fit for eligible salons and spas. Whether it's the right choice depends on how the business prices services and communicates with clients." },
      { q: "Do you offer gift card acceptance?",
        a: "Gift card acceptance is available depending on the setup. We'll walk through what's involved during onboarding." },
    ],
  },

  /* ══════════════════════════════════════════════════════════════════ */
  "service-businesses": {
    slug: "service-businesses",
    illustrationKey: "service",
    seo: {
      title: "Service Business Card Processing & Mobile Payments | Swipetronics",
      description:
        "Card processing, mobile payments, and invoicing options for service businesses — in-the-field payments, virtual terminal, and recurring billing setups.",
    },
    eyebrow: "For Service Businesses",
    h1: "Payment solutions for service businesses",
    heroBody:
      "Service businesses collect payment in a lot of different places — at a customer's home, on a job site, over the phone, or online. The right payment setup makes each of those feel consistent for the business owner and the customer.",
    challengesHeading: "What makes payments harder for a service business",
    challenges: [
      "Collecting payment on-site without carrying a full terminal in the truck.",
      "Handling phone or emailed invoices where the customer isn't there to swipe or tap a card.",
      "Managing recurring or scheduled charges for maintenance plans and service contracts.",
      "Keeping card-processing costs predictable across a mix of in-person, phone, and online payments.",
    ],
    howHeading: "How service businesses commonly accept payments",
    howBody:
      "Most service businesses use a mix of a mobile device (phone-based reader or handheld terminal), a virtual terminal for phone-in charges, and — where appropriate — recurring billing for maintenance plans.",
    howPoints: [
      { t: "In the field",  d: "Mobile payments through a phone-based reader (e.g., Clover Go) or handheld terminal (e.g., Dejavoo iPOSgo)." },
      { t: "Over the phone", d: "Virtual terminal for card-not-present transactions from the office." },
      { t: "For recurring plans", d: "Recurring billing for maintenance contracts or ongoing service." },
    ],
    solutionsHeading: "Relevant Swipetronics solutions",
    solutions: [
      { title: "Card processing",
        body:  "Card processing configured for a mix of in-person, phone, and online transactions.",
        to:    "/services" },
      { title: "Mobile devices",
        body:  "Handheld and phone-based payment devices — Clover Go and Dejavoo iPOSgo.",
        to:    "/mobile-devices" },
      { title: "Payment terminals",
        body:  "Countertop, handheld, and mobile devices from Valor, Dejavoo, and PAX.",
        to:    "/products" },
      { title: "Virtual terminal",
        body:  "Accept phone-in card payments from the office without a physical terminal at the counter.",
        to:    "/services" },
      { title: "Cash Discount / Dual Pricing",
        body:  "For eligible service businesses, Cash Discount can help reduce card-processing costs.",
        to:    "/cash-discount" },
    ],
    hardwareHeading: "Devices commonly used by service businesses",
    hardwareBody:
      "For service businesses, the right device depends on where transactions happen most — in the field, on the phone, or a mix of both.",
    hardwareItems: [
      { title: "Phone-based mobile payments", body: "A phone or tablet paired with a compact card reader (e.g., Clover Go)." },
      { title: "Handheld terminals",           body: "Purpose-built handhelds for in-the-field payments (e.g., Dejavoo iPOSgo)." },
      { title: "Countertop / office terminal", body: "For office-based invoicing and card-not-present transactions." },
    ],
    whyHeading: "Why the right payment setup matters",
    whyPoints: [
      "In-the-field payment collection speeds up cash flow — no waiting for the check to arrive.",
      "A dependable virtual terminal makes phone charges clean and consistent.",
      "Recurring billing keeps maintenance-plan revenue predictable and reduces missed invoices.",
    ],
    faqs: [
      { q: "Can we take payments on a phone or tablet in the field?",
        a: "Yes. Mobile payment devices like Clover Go pair with a phone or tablet so field techs can accept card payments at the job site." },
      { q: "Can we accept payment over the phone?",
        a: "Yes. A virtual terminal supports card-not-present transactions from the office — the customer's card can be charged over the phone without a physical terminal." },
      { q: "Can we set up recurring monthly charges?",
        a: "Yes. Recurring billing setups are available for maintenance contracts or other ongoing services." },
      { q: "Is Cash Discount a fit for a service business?",
        a: "Cash Discount / Dual Pricing may be a fit for eligible service businesses. Whether it's the right choice depends on invoice sizes and how the business communicates pricing to customers." },
    ],
  },
};

export function getIndustryData(slug) {
  return INDUSTRY_PAGES[slug] || null;
}
